import { WORLD_H, WORLD_W } from "../core/constants";
import { clamp } from "../core/sim";
import type { WorldRect } from "../core/types";

/**
 * The camera over the board. Pure arithmetic — the viewport arrives as two
 * numbers, so nothing here asks the DOM for anything and all of it is testable
 * without a browser.
 *
 * Centre-based rather than origin-based: the clamp is then one expression per
 * axis instead of a sign-dependent pair, and "keep the board on screen" is the
 * property that has to hold for every gesture.
 */

export interface View {
  /** CSS px per world unit at zoom 1: the whole board just fits. */
  readonly fit: number;
  /** User zoom over fit. 1 = whole board visible. */
  readonly zoom: number;
  /** World coordinate held at the centre of the viewport. */
  readonly cx: number;
  readonly cy: number;
}

export interface Point {
  x: number;
  y: number;
}

export const MIN_ZOOM = 1;

/**
 * Derived, not chosen. NODE_HIT_R is 33 world units, and at the fit scale of
 * the narrowest supported viewport (320 px → 0.32) it takes a factor of
 * 44 / (33 × 0.32) = 4.2 for the hit target to clear the 44 px floor. Four
 * lands at 42 px there and 46 px at 390 px, which is the width RFC-0009
 * measures against.
 */
export const MAX_ZOOM = 4;

export const fitScale = (vw: number, vh: number): number =>
  Math.min(vw / WORLD_W, vh / WORLD_H);

/** The whole board, centred. The default, and what "zoom out" returns to. */
export const fitView = (vw: number, vh: number): View => ({
  fit: fitScale(vw, vh),
  zoom: MIN_ZOOM,
  cx: WORLD_W / 2,
  cy: WORLD_H / 2,
});

/**
 * Clamps zoom into range and the centre so the viewport shows nothing outside
 * the board.
 *
 * On an axis where the visible span is wider than the board there is no valid
 * range to clamp into — the low bound would sit above the high one. The centre
 * is pinned to the middle of the board instead, which is what letterboxes it.
 */
export function clampView(v: View, vw: number, vh: number, bounds: WorldRect = { x: 0, y: 0, width: WORLD_W, height: WORLD_H }): View {
  const zoom = clamp(v.zoom, MIN_ZOOM, MAX_ZOOM);
  const scale = v.fit * zoom;
  const spanX = vw / scale;
  const spanY = vh / scale;

  return {
    fit: v.fit,
    zoom,
    cx: spanX >= bounds.width ? bounds.x + bounds.width / 2 : clamp(v.cx, bounds.x + spanX / 2, bounds.x + bounds.width - spanX / 2),
    cy: spanY >= bounds.height ? bounds.y + bounds.height / 2 : clamp(v.cy, bounds.y + spanY / 2, bounds.y + bounds.height - spanY / 2),
  };
}

export function focusRegion(v: View, index: number): View {
  return { ...v, cx: index === 0 ? WORLD_W / 2 : WORLD_W + (index - 0.5) * 600, cy: WORLD_H / 2 };
}

/**
 * Neither direction clamps its result. A screen point outside the canvas maps
 * to a world point outside the board on purpose: a gesture computes its anchor
 * first and only then asks whether the view may go there.
 */
export function toWorld(v: View, vw: number, vh: number, sx: number, sy: number): Point {
  const scale = v.fit * v.zoom;
  return { x: v.cx + (sx - vw / 2) / scale, y: v.cy + (sy - vh / 2) / scale };
}

export function toScreen(v: View, vw: number, vh: number, wx: number, wy: number): Point {
  const scale = v.fit * v.zoom;
  return { x: vw / 2 + (wx - v.cx) * scale, y: vh / 2 + (wy - v.cy) * scale };
}

/**
 * Zooms by `factor` while holding still the world point currently under
 * (sx, sy) — the anchor is what makes a pinch feel attached to the fingers
 * rather than to the middle of the screen.
 *
 * The anchor survives only on axes the clamp leaves free; where the board is
 * narrower than the viewport, centring wins by construction.
 */
export function zoomAround(
  v: View, vw: number, vh: number, sx: number, sy: number, factor: number,
  bounds: WorldRect = { x: 0, y: 0, width: WORLD_W, height: WORLD_H },
): View {
  const anchor = toWorld(v, vw, vh, sx, sy);
  const zoom = clamp(v.zoom * factor, MIN_ZOOM, MAX_ZOOM);
  const scale = v.fit * zoom;

  return clampView(
    {
      fit: v.fit,
      zoom,
      cx: anchor.x - (sx - vw / 2) / scale,
      cy: anchor.y - (sy - vh / 2) / scale,
    },
    vw,
    vh, bounds,
  );
}

/** Pans by a screen-space delta. Positive dx moves the board right. */
export function panBy(v: View, vw: number, vh: number, dx: number, dy: number, bounds: WorldRect = { x: 0, y: 0, width: WORLD_W, height: WORLD_H }): View {
  const scale = v.fit * v.zoom;
  return clampView({ ...v, cx: v.cx - dx / scale, cy: v.cy - dy / scale }, vw, vh, bounds);
}
